import { AppBar, Box, Button, Dialog, IconButton, Toolbar } from '@mui/material'
import { IconClose } from 'assets/images'
import { IconGoogleMapPin } from 'assets/images/google-map-pin'
import { SelectInput } from 'components'
import { COLUMNS } from 'constants/common'
import { useFormikContext } from 'formik'
import { useImageFit } from 'hooks/useImageFit'
import { t } from 'i18n'
import { isEmpty } from 'lodash'
import {
  FullImgPreview,
  SmallImgPreview,
  StyledLoading
} from 'pages/Slopes/styled'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { getResort } from 'services'
import { ApplicationState } from 'store'
import {
  HeatmapDefinition,
  MarkerPosition,
  ReportState,
  UserInfo
} from 'store/ducks'
import { removeHeatmapLocation } from 'store/ducks/report/actions'
import styled from 'styled-components'

interface Props {
  user: UserInfo
  report: ReportState
  handleOnChange: (
    coordinates: MarkerPosition & { reportId: string | undefined }
  ) => void
  handleClearPosition: () => void
  coordinates: MarkerPosition
}

// Image sizing handled by useImageFit

const ImageWrapper = styled(Box)<{ $width: number; $height?: number }>`
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => ($height ? `${$height}px` : 'auto')};
  max-height: 80vh;
  background-color: #eeeeee;
  position: relative;
`

const HeatMapWrapper = styled.div`
  // display: flex;
  // gap: 1rem;
  margin-bottom: 2rem;
  // align-items: flex-end;
`

const AddLocationHeatmap = ({
  user,
  report,
  handleOnChange,
  handleClearPosition,
  coordinates
}: Props) => {
  const [preview, setPreview] = useState('')
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const { displayWidth, displayHeight, imgLoading } = useImageFit(
    preview,
    open,
    { thumbnailMaxWidth: 450, thumbnailMaxHeight: 450 }
  )
  const [markerPosition, setMarkerPosition] =
    useState<MarkerPosition>(coordinates)
  const dispatch = useDispatch()

  const SK = report?.data?.SK
  const season = report?.data?.season || ''
  const [resortHeatmaps, setResortHeatmaps] = useState<HeatmapDefinition[]>([])
  const [heatmapsLoading, setHeatmapsLoading] = useState<boolean>(true)

  useEffect(() => {
    const fetchResort = async () => {
      if (!user?.resortId) {
        setHeatmapsLoading(false)
        return
      }
      try {
        setHeatmapsLoading(true)
        const fresh = await getResort(user.resortId)
        setResortHeatmaps((fresh.heatmaps || []) as HeatmapDefinition[])
      } catch (e) {
        console.error('Failed to fetch resort for heatmaps', e)
      } finally {
        setHeatmapsLoading(false)
      }
    }
    fetchResort().catch(console.error)
  }, [user?.resortId])

  const targetSeason = useMemo(() => season.toLowerCase(), [season])
  const seasonHeatmaps: HeatmapDefinition[] = useMemo(
    () =>
      (resortHeatmaps || []).filter(
        h => h.season.toLowerCase() === targetSeason
      ),
    [resortHeatmaps, targetSeason]
  )
  const { values, setFieldValue } = useFormikContext<any>()
  const formHeatmapId: string | undefined = values?.heatmap?.heatmapId

  // Sizing is fully handled by useImageFit

  const handleUpload = useCallback(async () => {
    // Load URL and dimensions for selected heatmap (no server call; URL is enriched by backend)
    setLoading(true)
    try {
      const selected =
        seasonHeatmaps.find(h => h.id === formHeatmapId) ||
        seasonHeatmaps.find(h => h.isDefault)
      if (!selected) {
        setPreview('')
        // sizing handled by useImageFit
        return
      }
      setPreview(selected.url)
      const img = new Image()
      img.src = selected.url
      img.onload = () => {
        // Natural dimensions handled by useImageFit
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load heatmap', {
        position: 'top-right'
      })
    } finally {
      setLoading(false)
    }
  }, [seasonHeatmaps, formHeatmapId])

  useEffect(() => {
    // Initialize default selection based on isDefault or first available for season
    if (!formHeatmapId && seasonHeatmaps.length > 0) {
      const def =
        seasonHeatmaps.find(h => h.isDefault)?.id || seasonHeatmaps[0]?.id
      if (def) setFieldValue('heatmap.heatmapId', def)
    }
  }, [seasonHeatmaps, formHeatmapId, setFieldValue])

  useEffect(() => {
    // Whenever selection changes, refresh preview/dimensions
    if (formHeatmapId) {
      handleUpload().catch(console.error)
    }
  }, [formHeatmapId, handleUpload])

  // Natural size loading is handled by useImageFit

  // Don't early return; we want to render the heatmap selector even if image hasn't loaded yet

  const handleClickOpen = () => {
    setOpen(true)
  }

  const saveCoordinates = ({ x, y }: { x: number; y: number }) => {
    const coordinates = {
      reportId: SK,
      x,
      y
    }
    setMarkerPosition({ x, y })
    handleOnChange(coordinates)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const clearPosition = () => {
    setMarkerPosition({})
    handleClearPosition()
    dispatch(removeHeatmapLocation())
  }

  const cellSize = displayWidth / COLUMNS
  const rows = Math.ceil(displayHeight / cellSize)
  const cells = Array(COLUMNS * rows).fill(null) as never[]

  const mapPinProps = () => {
    const width = 26
    const height = 45
    return {
      style: {
        position: 'absolute' as const,
        top: markerPosition.y * cellSize + cellSize / 2 - height,
        left: markerPosition.x * cellSize + cellSize / 2 - width / 2,
        zIndex: 9999
      },
      width,
      height
    }
  }

  return (
    <div>
      <h2>{t('resortMapLocation')}</h2>

      {seasonHeatmaps.length > 0 && (
        <SelectInput
          name="heatmap.heatmapId"
          label={'Heatmap'}
          translateText={false}
          options={seasonHeatmaps.map(h => ({ id: h.id, label: h.name }))}
          value={formHeatmapId}
          onChange={(e: any) => {
            // Changing heatmap resets marker and sets new heatmapId
            setMarkerPosition({})
            setFieldValue('heatmap.heatmapId', e.target.value)
            // Clear any previously set coordinates for safety
            setFieldValue('heatmap.coordinates', undefined)
          }}
          style={{ maxWidth: 450, marginBottom: '0.75rem' }}
        />
      )}

      {(preview || heatmapsLoading || seasonHeatmaps.length > 0) && (
        <HeatMapWrapper>
          <ImageWrapper
            $width={displayWidth}
            $height={displayHeight || undefined}
          >
            {(loading || imgLoading) && <StyledLoading />}
            <Dialog open={open} fullScreen>
              <AppBar
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingLeft: '2rem'
                }}
              >
                <span>{t('clickOnTheMapToRegister')}</span>
                <Toolbar>
                  <IconButton
                    autoFocus
                    edge="start"
                    color="inherit"
                    onClick={handleClose}
                    aria-label="close"
                  >
                    <IconClose />
                  </IconButton>
                </Toolbar>
              </AppBar>
              <div
                style={{
                  display: 'flex',
                  margin: '0 auto',
                  position: 'relative',
                  width: displayWidth,
                  height: displayHeight
                }}
              >
                <FullImgPreview
                  src={preview}
                  alt={t('preview')}
                  style={{ width: displayWidth, height: displayHeight }}
                />
                {cells.map((_, index) => {
                  const row = Math.floor(index / COLUMNS)
                  const col = index % COLUMNS
                  return (
                    <div
                      key={index}
                      style={{
                        position: 'absolute',
                        top: row * cellSize,
                        left: col * cellSize,
                        width: cellSize,
                        height: cellSize
                      }}
                      onClick={() => saveCoordinates({ x: col, y: row })}
                    />
                  )
                })}
                {!isEmpty(markerPosition) && (
                  <IconGoogleMapPin {...mapPinProps()} />
                )}
              </div>
            </Dialog>
            {preview ? (
              <div onClick={handleClickOpen} style={{ cursor: 'pointer' }}>
                <SmallImgPreview src={preview} alt={t('preview')} />
                {!isEmpty(markerPosition) && (
                  <IconGoogleMapPin {...mapPinProps()} />
                )}
              </div>
            ) : (
              <div
                style={{
                  height: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {heatmapsLoading ? (
                  <StyledLoading />
                ) : (
                  <span>{t('noImage')}</span>
                )}
              </div>
            )}
          </ImageWrapper>

          {!isEmpty(markerPosition) && preview && (
            <Button onClick={clearPosition} variant="contained" size="small">
              {t('removeLocation')}
            </Button>
          )}
        </HeatMapWrapper>
      )}
    </div>
  )
}

const mapStateToProps = (state: ApplicationState) => ({
  user: state.auth.data,
  report: state.report
})

export default connect(mapStateToProps, null)(AddLocationHeatmap)
